import { useContext } from 'react'
import styled from 'styled-components'
import CreationToolOutputType, { outputTypeIcon } from '../../../../types/output-type'
import CreationToolStatus from '../../../../types/status'
import { SquareButton } from '../../../buttons'
import { CreationToolUploadContext } from '../../manager'
import { CreationToolUploadSource } from '../../types'
import { ErrorIcon } from '../icons'
import { DragIcon as DragIcon_ } from './icon'

const Item = styled.div`
    flex-shrink: 0;
    margin-left: 6px;

    &:last-child {
        margin-right: 10px;
    }
`

const IconItem = styled(Item)`
    margin-left: 10px;

    svg {
        display: block;
        height: 22px;
        opacity: 0.85;
    }
`

const Button = styled(SquareButton)`
    background: #000;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    min-width: 32px;
    padding: 0 8px;
    position: relative;
    width: auto;

    span {
        display: inline-block;
        font-weight: bold;
        font-size: 15px;
        padding: 0 5px;
    }

    i {
        display: block;
        height: 12px;
        line-height: 1;
    }
`

const ErrorButton = styled(Button)`
    svg {
        height: 16px;
    }
`

const DragIcon = styled(DragIcon_)`
    pointer-events: none;
    opacity: 0.5;
    flex-shrink: 0;
`

const ArrowButton = styled(Button)`
    background: transparent;

    i {
        transition: transform 0.2s ease-out;
    }
`

type Props = {
    source: CreationToolUploadSource
    isPreviewing: boolean
    isHovered: boolean
    isEditing: boolean
    onTogglePreview: (isPreviewing: boolean) => void
}

const CreationUploadItemButtons = ({ source, isPreviewing, onTogglePreview, isHovered, isEditing }: Props) => {
    const { error, status, uploadedId, outputType } = source
    const { uploadSources } = useContext(CreationToolUploadContext)
    const OutputTypeIcon = outputTypeIcon(outputType)

    // uploaded
    if (uploadedId) {
        const typeTitle = CreationToolOutputType.title(outputType)
        const basePath = CreationToolOutputType.uploadedBasePath(outputType)
        return (
            <Item>
                <Button as="a" href={`${basePath}/${uploadedId}`} rel="noopener noreferrer" target="_blank">
                    <span>Open {typeTitle}</span>
                    <i className="ss-action" />
                </Button>
            </Item>
        )
    }

    // uploading
    if (status !== CreationToolStatus.Idle) {
        return (
            <Item>
                <Button style={{ pointerEvents: 'none' }}>
                    <span>{CreationToolStatus.label(status)}...</span>
                </Button>
            </Item>
        )
    }

    return (
        <>
            {error && (
                <Item>
                    <ErrorButton onClick={() => onTogglePreview(true)}>
                        <ErrorIcon />
                        <span>Error</span>
                    </ErrorButton>
                </Item>
            )}
            {uploadSources.length > 1 && !error && isHovered && !isPreviewing && !isEditing && <DragIcon />}
            <IconItem style={{ pointerEvents: 'none' }}>
                <OutputTypeIcon />
            </IconItem>
            <Item>
                <ArrowButton onClick={() => onTogglePreview(!isPreviewing)}>
                    <i className="ss-navigatedown" style={{ transform: `rotate(${isPreviewing ? 180 : 0}deg)` }} />
                </ArrowButton>
            </Item>
        </>
    )
}

export default CreationUploadItemButtons
